const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Global shared IntersectionObserver for scroll triggers
let scrollTriggerObserver = null;

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    const elementTarget = element.target;

    if (element.isIntersecting) {
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade') && !elementTarget.style.getPropertyValue('--animation-order')) {
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
        }
      }
      elementTarget.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
      elementTarget.setAttribute('data-revealed', 'true');

      // Unobserve immediately so animation triggers strictly once during downward scroll
      observer.unobserve(elementTarget);

      // Clean up transform once animation completed so natural document flow and sticky elements work
      const cleanupAnimation = () => {
        if (elementTarget.classList.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME)) {
          elementTarget.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
        }
      };
      elementTarget.addEventListener('animationend', cleanupAnimation, { once: true });
      setTimeout(cleanupAnimation, 1200);
    }
  });
}

function applyGlobalSectionTriggers(rootEl = document) {
  try {
    let container = rootEl;
    if (rootEl === document) {
      container = document.querySelector('#MainContent, .content-for-layout, [role="main"]') || document.body;
    }
    if (!container) return;

    // Handle single section passed via Theme Editor events
    const isSingleSection =
      rootEl !== document &&
      rootEl.nodeType === 1 &&
      (rootEl.classList.contains('shopify-section') || (rootEl.id && rootEl.id.startsWith('shopify-section-')));

    const sections = isSingleSection
      ? [rootEl]
      : Array.from(
          container.querySelectorAll(
            '.shopify-section, #MainContent > section, #MainContent > div[id^="shopify-section-"], footer.footer, .shopify-section-group-footer-group .shopify-section'
          )
        );

    sections.forEach((section) => {
      // Exclude ineligible surfaces: header groups, overlay/drawers, popups, floating chat, mobile bottom nav
      if (
        section.closest(
          '.shopify-section-group-header-group, .shopify-section-group-overlay-group, .header-wrapper, #header-group, aside, .cart-drawer, .search-drawer, .parent-expert-chat'
        ) ||
        section.classList.contains('shopify-section-header') ||
        section.classList.contains('shopify-section-announcement-bar') ||
        section.classList.contains('mobile-bottom-nav') ||
        section.classList.contains('parent-expert-chat') ||
        (section.id && (section.id.includes('mobile-bottom-nav') || section.id.includes('parent-expert-chat'))) ||
        section.hasAttribute('aria-hidden')
      ) {
        return;
      }

      // Check if section itself or any element inside it already has scroll-trigger
      const hasInternalScrollTrigger =
        section.classList.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME) ||
        section.querySelector(`.${SCROLL_ANIMATION_TRIGGER_CLASSNAME}`) !== null;

      if (!hasInternalScrollTrigger) {
        section.classList.add(SCROLL_ANIMATION_TRIGGER_CLASSNAME, 'animate--slide-in');
      }
    });
  } catch (e) {
    console.error('Error applying section animation triggers:', e);
  }
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.documentElement.classList.add('scroll-animations-ready');
    return;
  }

  applyGlobalSectionTriggers(rootEl);

  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) {
    document.documentElement.classList.add('scroll-animations-ready');
    return;
  }

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  if (!scrollTriggerObserver) {
    scrollTriggerObserver = new IntersectionObserver(onIntersection, {
      rootMargin: '0px 0px -40px 0px',
      threshold: 0,
    });
  }

  const isInitialPageLoad = rootEl === document;

  if (isInitialPageLoad) {
    const mainContent = document.querySelector('#MainContent, .content-for-layout, [role="main"]') || document.body;
    const firstSection = mainContent ? (mainContent.querySelector('.shopify-section') || mainContent.firstElementChild) : null;
    const isAtPageTop = window.scrollY === 0;

    animationTriggerElements.forEach((element) => {
      if (element.hasAttribute('data-revealed')) {
        return;
      }

      // If page is opened at the top, show the topmost first section immediately without delay/shift
      const isTopFoldElement =
        isAtPageTop &&
        firstSection &&
        (element === firstSection || firstSection.contains(element));

      // If page is loaded already scrolled down past this element
      const bounds = element.getBoundingClientRect();
      const isAlreadyPassed = !isAtPageTop && bounds.bottom <= 0;

      if (isTopFoldElement || isAlreadyPassed) {
        element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        element.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
        element.setAttribute('data-revealed', 'true');
      } else {
        element.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        element.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
        scrollTriggerObserver.observe(element);
      }
    });

    document.documentElement.classList.add('scroll-animations-ready');
  } else {
    // Dynamic or re-rendered sections (Theme Editor or MutationObserver)
    animationTriggerElements.forEach((element) => {
      if (!element.hasAttribute('data-revealed')) {
        element.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        element.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
        scrollTriggerObserver.observe(element);
      }
    });
  }
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.initializeScrollAnimationTrigger = initializeScrollAnimationTrigger;

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();

  // Support dynamically rendered sections inserted into MainContent
  if ('MutationObserver' in window) {
    const mainContent = document.querySelector('#MainContent, .content-for-layout, [role="main"]');
    if (mainContent) {
      const observer = new MutationObserver((mutations) => {
        let hasNewSection = false;
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (
              node.nodeType === 1 &&
              (node.classList?.contains('shopify-section') || node.querySelector?.('.shopify-section'))
            ) {
              hasNewSection = true;
            }
          });
        });
        if (hasNewSection) {
          initializeScrollAnimationTrigger(mainContent);
        }
      });
      observer.observe(mainContent, { childList: true, subtree: false });
    }
  }
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
